import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setToken, tryRefresh } from "@/lib/api";
import { logWarn } from "@/lib/log";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refreshUser = useCallback(async () => {
    const me = () => api.get("/auth/me").then((r) => r.data);
    try {
      const data = await me().catch(async (e) => {
        if (e.response?.status !== 401) throw e;
        await tryRefresh();
        return me();
      });
      setUser(data);
      return data;
    } catch (_) {
      setToken(null);
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Skip /me while returning from Google OAuth; AuthCallback establishes the session first.
    if (window.location.hash?.includes("session_id=")) return;
    refreshUser();
  }, [refreshUser]);

  const applyAuth = useCallback((data) => {
    setToken(data.token);
    setUser(data.user);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    applyAuth(data, "back");
    return data.user;
  }, [applyAuth]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    applyAuth(data, "new");
    return data.user;
  }, [applyAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { logWarn("Logout lato server non riuscito", e); }
    setToken(null);
    sessionStorage.removeItem("aurora_greeted");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, refreshUser, login, register, logout, applyAuth }),
    [user, refreshUser, login, register, logout, applyAuth]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
